// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { orgDefaultCurrencyCodeSchema } from '@/constants/currency';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { z } from 'zod';

const PATH = '/v1/asset/currencies';

const responseSchema = z.object({
  data: z.object({
    // Keep only app-allowed codes: the report endpoints reject anything outside this set, so a
    // legacy stored currency (or any recognized-but-unsupported code) must not reach the picker.
    currencies: z.array(isoCurrencyCodeSchema).transform((codes) =>
      codes.flatMap((code) => {
        const parsed = orgDefaultCurrencyCodeSchema.safeParse(code);
        return parsed.success ? [parsed.data] : [];
      })
    ),
  }),
  metadata: responseMetadataSchema,
});

/**
 * Fetches the distinct cost currencies the organization uses across its assets,
 * powering the report currency picker.
 */
export async function getOrganizationAssetCurrencies(): Promise<
  IsoCurrencyCode[]
> {
  const response = await api.get(PATH);
  const result = await parseResponse(responseSchema, response.data, {
    schemaName: 'OrganizationAssetCurrencies',
    endpoint: PATH,
  });
  return result.data.currencies;
}
